import { useEffect, useState, type ReactNode } from 'react';

// Plan 048: on the public desk only the owner's passkey gets in. The server decides; this screen only asks for the
// passkey (Face ID on the phone) and, the first time, for the one-time setup code made on the server.
type AuthState = { required: boolean; signed_in: boolean; set_up: boolean };

const b64 = (data: ArrayBuffer) => btoa(String.fromCharCode(...new Uint8Array(data))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const unb64 = (text: string) => Uint8Array.from(atob(text.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));

async function post(path: string, body?: unknown) {
  const r = await fetch(path, { method: 'POST', cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: body === undefined ? undefined : JSON.stringify(body) });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof json.detail === 'string' ? json.detail : 'The desk could not be reached');
  return json;
}

async function createPasskey(code: string) {
  const o = await post('/api/v1/auth/register/options', { setup_code: code });
  const credential = await navigator.credentials.create({ publicKey: {
    ...o, challenge: unb64(o.challenge), user: { ...o.user, id: unb64(o.user.id) },
    excludeCredentials: (o.excludeCredentials ?? []).map((c: { id: string; type: 'public-key' }) => ({ ...c, id: unb64(c.id) })),
  } }) as PublicKeyCredential | null;
  if (!credential) throw new Error('No passkey was made');
  const r = credential.response as AuthenticatorAttestationResponse;
  return post('/api/v1/auth/register', {
    id: credential.id, rawId: b64(credential.rawId), type: credential.type,
    response: { clientDataJSON: b64(r.clientDataJSON), attestationObject: b64(r.attestationObject) },
  });
}

async function usePasskey() {
  const o = await post('/api/v1/auth/sign-in/options');
  const credential = await navigator.credentials.get({ publicKey: {
    ...o, challenge: unb64(o.challenge),
    allowCredentials: (o.allowCredentials ?? []).map((c: { id: string; type: 'public-key' }) => ({ ...c, id: unb64(c.id) })),
  } }) as PublicKeyCredential | null;
  if (!credential) throw new Error('No passkey was used');
  const r = credential.response as AuthenticatorAssertionResponse;
  return post('/api/v1/auth/sign-in', {
    id: credential.id, rawId: b64(credential.rawId), type: credential.type,
    response: {
      clientDataJSON: b64(r.clientDataJSON), authenticatorData: b64(r.authenticatorData), signature: b64(r.signature),
      userHandle: r.userHandle ? b64(r.userHandle) : null,
    },
  });
}

export function Gate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const check = () => fetch('/api/v1/auth/session', { cache: 'no-store' }).then(r => r.ok ? r.json() : null)
      // A desk without the sign-in endpoint (an older server, or test fixtures) is a loopback desk.
      .then((s: AuthState | null) => setState(s ?? { required: false, signed_in: true, set_up: true }))
      .catch(() => setState(s => s ?? { required: false, signed_in: true, set_up: true }));
    check();
    // A session that ends while the desk is open (signed out elsewhere, or expired) brings this screen back.
    const timer = setInterval(check, 60_000);
    return () => clearInterval(timer);
  }, []);
  if (state == null) return null;
  if (!state.required || state.signed_in) return <>{children}</>;
  const go = (action: () => Promise<AuthState>) => {
    setBusy(true); setError(null);
    action().then(setState).catch((e: Error) => setError(e.name === 'NotAllowedError' ? 'Cancelled. Try again when ready.' : e.message))
      .finally(() => setBusy(false));
  };
  return <main className="sign-in" data-testid="sign-in">
    <h1>Crypto Thunder</h1>
    {state.set_up ? <>
      <p>Sign in to trade.</p>
      <button className="go" disabled={busy} onClick={() => go(usePasskey)} data-testid="use-passkey">{busy ? 'Waiting for your passkey…' : 'Sign in with passkey'}</button>
    </> : <>
      <p>Set up this desk: enter the setup code from the server, then save a passkey on this device.</p>
      <input value={code} onChange={e => setCode(e.target.value)} placeholder="Setup code" autoComplete="one-time-code" autoCapitalize="none" spellCheck={false} data-testid="setup-code"/>
      <button className="go" disabled={busy || code.trim().length < 8} onClick={() => go(() => createPasskey(code))} data-testid="create-passkey">{busy ? 'Waiting for your passkey…' : 'Create passkey'}</button>
    </>}
    {error && <p className="down" role="alert" data-testid="sign-in-error">{error}</p>}
  </main>;
}
